import React, { startTransition } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import i18n from "../src/i18n";
import store from "./redux/store";
import App from "./App";
import { ToastProvider } from "./components/ToastProvider";
import CacheHandler from "./components/CacheHandler";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 1000 * 30, // 30 segundos
    },
  },
});

const Root = () => {
  return (
    <CacheHandler>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <Router>
              <ToastProvider>
                <App />
              </ToastProvider>
            </Router>
          </I18nextProvider>
        </QueryClientProvider>
      </Provider>
    </CacheHandler>
  );
};

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);

// REACT_APP_PLAYWRIGHT_TEST is only set when Playwright runs the webServer
const isPlaywrightE2E = process.env.REACT_APP_PLAYWRIGHT_TEST === "true";

startTransition(() => {
  root.render(
    isPlaywrightE2E ? (
      <Root />
    ) : (
      <React.StrictMode>
        <Root />
      </React.StrictMode>
    ),
  );
});

// Service Worker PWA
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    if (process.env.NODE_ENV === "development") {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          if (
            registration.scope.includes("localhost") ||
            registration.scope.includes("192.168")
          ) {
            registration.unregister();
          }
        });
      });
    }

    navigator.serviceWorker
      .register("/sw.js")
      .then(() => {
        // Service Worker registered successfully
      })
      .catch(() => {
        // Service Worker registration failed
      });
  });
}

export default Root;
